import { useState, useEffect, useMemo, useCallback } from 'react';
import { getScrollContainer } from '../utils/scrollContainer';
import { createPortal } from 'react-dom';
import { useSelector } from '@legendapp/state/react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { todayActivity$, auth$, getTotalHighlightCount, getTotalCommentCount } from '../store';
import { ICON_SIZE, MetricValue } from '../design-system';
import PredictionModal from './PredictionModal';
import './ReadingHeatmap.css';

// Helper component to render month labels with positioning logic
const MonthLabels = ({ monthLabels, variant = 'sidebar' }) => {
  const isDashboard = variant === 'dashboard';
  const WEEKS = 18;

  return (
    <div className="graph-months">
      {monthLabels.map((m, i) => {
        let labelStyle;

        if (isDashboard) {
          // Dashboard: percentage-based positioning to match CSS Grid 1fr columns
          const pct = (m.weekIndex / WEEKS) * 100;
          labelStyle = { left: `${pct}%` };

          // Skip first label if too close to second (< 15% apart)
          if (i === 0 && monthLabels.length > 1) {
            const nextPct = (monthLabels[1].weekIndex / WEEKS) * 100;
            if (nextPct - pct < 15) return null;
          }

          // Last label: right-align if past 90%
          const isLast = i === monthLabels.length - 1;
          if (isLast && pct > 90) {
            labelStyle = { right: '0', left: 'auto', transform: 'none' };
          }
        } else {
          // Sidebar: fixed pixel positioning (original behavior)
          let labelPos = m.weekIndex * 11 - 2;
          labelStyle = { left: `${labelPos}px` };

          if (i === 0 && monthLabels.length > 1) {
            const nextMonthPos = monthLabels[1].weekIndex * 11 - 2;
            if (nextMonthPos - labelPos < 30) return null;
          }

          const isLast = i === monthLabels.length - 1;
          if (isLast && labelPos > 168) {
            labelStyle = { right: '0', left: 'auto', transform: 'none' };
          }
        }

        return (
          <div key={i} className="month-label" style={labelStyle}>
            {m.label}
          </div>
        );
      })}
    </div>
  );
};

// Stat label with skeleton value - for loading state
const StatWithSkeleton = ({ label, title }) => (
  <div className="heatmap-stat heatmap-stat--loading" title={title || undefined}>
    <div className="skeleton-value"></div>
    <span className="stat-label">{label}</span>
  </div>
);

// Stat labels with skeleton values component - for loading state
const StatLabelsSkeleton = ({ t }) => (
  <div className="heatmap-header">
    <StatWithSkeleton label={t('heatmap.blocks')} />
    <StatWithSkeleton label={t('heatmap.marks')} title={t('heatmap.marksTitle')} />
    <StatWithSkeleton label={t('heatmap.days')} />
  </div>
);

const ReadingHeatmap = ({ listId = null, userId = null, variant = 'sidebar', onStatsChange }) => {
  const { t, i18n } = useTranslation('sidebar');
  const isSidebar = variant === 'sidebar';
  const isProfile = variant === 'profile';
  const variantClass = variant === 'dashboard'
    ? 'variant-dashboard'
    : isProfile ? 'variant-profile' : '';
  const [activityData, setActivityData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tooltip, setTooltip] = useState({ visible: false, date: '', count: 0, x: 0, y: 0 });
  const [showPrediction, setShowPrediction] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(
    () => variant === 'sidebar' && localStorage.getItem('reedle_heatmap_collapsed') !== 'false'
  );

  const toggleCollapse = useCallback(() => {
    setIsCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('reedle_heatmap_collapsed', String(next));
      return next;
    });
  }, []);

  // Get today's blocks from local store for immediate updates
  // Only use for current user and when not filtering by list
  const currentUserId = useSelector(() => auth$.user.get()?.id);
  const localTodayBlocks = useSelector(() => todayActivity$.todayBlocks.get());
  const shouldUseLocalToday = !listId || listId === 'all';
  const isViewingOwnActivity = !userId || userId === currentUserId;

  // Get marks count (highlights + comments) - reactive
  const highlightCount = useSelector(() => getTotalHighlightCount());
  const commentCount = useSelector(() => getTotalCommentCount());
  const marksCount = highlightCount + commentCount;

  // Fetch activity data
  useEffect(() => {
    const fetchActivity = async () => {
      setLoading(true);
      try {
        // Get data from last 18 weeks
        const weeksAgo = new Date();
        weeksAgo.setDate(weeksAgo.getDate() - 18 * 7);

        let query = supabase
          .from('reedle_reading_activity')
          .select('hour_bucket, block_count, article_id')
          .gte('hour_bucket', weeksAgo.toISOString());

        // Filter by user if specified (for viewing other members' progress)
        if (userId) {
          query = query.eq('user_id', userId);
        }

        // If filtering by list (and it's a valid UUID, not 'all'), filter via junction table
        if (listId && listId !== 'all') {
          // Get article IDs in this list from the junction table
          const { data: junctionRows } = await supabase
            .from('reedle_article_lists')
            .select('article_id')
            .eq('list_id', listId);

          const articleIds = (junctionRows || []).map(r => r.article_id);

          if (articleIds.length === 0) {
            // No articles in this list — skip the main query
            setActivityData([]);
            setLoading(false);
            return;
          }

          query = supabase
            .from('reedle_reading_activity')
            .select('hour_bucket, block_count, article_id')
            .gte('hour_bucket', weeksAgo.toISOString())
            .in('article_id', articleIds);

          if (userId) {
            query = query.eq('user_id', userId);
          }
        }

        const { data, error } = await query;
        if (error) throw error;
        setActivityData(data || []);
      } catch (err) {
        console.error('Failed to fetch reading activity:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchActivity();
  }, [listId, userId]);

  // Aggregate data by local date, with local override for today
  const { dateMap, stats } = useMemo(() => {
    const map = {};
    let totalBlocks = 0;
    const uniqueDays = new Set();
    const todayString = new Date().toLocaleDateString('sv'); // "YYYY-MM-DD"

    activityData.forEach(row => {
      // Convert to local date
      const localDate = new Date(row.hour_bucket).toLocaleDateString('sv');
      map[localDate] = (map[localDate] || 0) + (row.block_count || 0);
      totalBlocks += row.block_count || 0;
      if (row.block_count > 0) uniqueDays.add(localDate);
    });

    // Override today's count with local store value for immediate feedback
    // Only when viewing own activity without list filter
    if (shouldUseLocalToday && isViewingOwnActivity) {
      const serverTodayBlocks = map[todayString] || 0;
      // Use local value, which should be >= server value
      // The local value updates immediately, server syncs periodically
      if (localTodayBlocks !== serverTodayBlocks) {
        // Adjust total to reflect local count
        totalBlocks = totalBlocks - serverTodayBlocks + localTodayBlocks;
        map[todayString] = localTodayBlocks;
      }
      if (localTodayBlocks > 0) uniqueDays.add(todayString);
    }

    return {
      dateMap: map,
      stats: { totalBlocks, dayCount: uniqueDays.size }
    };
  }, [activityData, localTodayBlocks, shouldUseLocalToday, isViewingOwnActivity]);

  // Tooltip handlers
  const showTooltip = (day, event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltip({
      visible: true,
      date: day.date,
      count: day.count,
      x: rect.left + rect.width / 2,  // Center of cell
      y: rect.top - 8,  // Above cell
    });
  };

  const hideTooltip = useCallback(() => {
    setTooltip(prev => ({ ...prev, visible: false }));
  }, []);

  const handleCellClick = (day, event) => {
    event.stopPropagation();  // Prevent triggering heatmap click (prediction modal)
    if (tooltip.visible && tooltip.date === day.date) {
      hideTooltip();  // Toggle off if same cell
    } else {
      showTooltip(day, event);  // Show tooltip
    }
  };

  // Hide tooltip on click outside or scroll
  useEffect(() => {
    if (!tooltip.visible) return;

    const handleClickOutside = () => hideTooltip();
    const handleScroll = () => hideTooltip();

    const scrollContainer = getScrollContainer();
    document.addEventListener('click', handleClickOutside);
    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      document.removeEventListener('click', handleClickOutside);
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, [tooltip.visible, hideTooltip]);

  // Generate graph data: today is always in the last column
  // Today's row position = today's day of week (0=Sun at top, 6=Sat at bottom)
  const generateMonthLabels = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const weeks = 18;
    const todayDayOfWeek = today.getDay();

    const totalDaysFromStart = (weeks - 1) * 7 + todayDayOfWeek;
    const startDate = new Date(today);
    startDate.setDate(today.getDate() - totalDaysFromStart);

    const monthLabels = [];
    let currentMonth = -1;

    for (let w = 0; w < weeks; w++) {
      for (let d = 0; d < 7; d++) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + (w * 7) + d);
        const m = date.getMonth();
        if (m !== currentMonth) {
          if (w < weeks - 1 || d === 0) {
            monthLabels.push({
              weekIndex: w,
              dayIndex: d,
              label: date.toLocaleString(i18n.language, { month: 'short' })
            });
          }
          currentMonth = m;
        }
      }
    }

    return monthLabels;
  }, [i18n.language]);

  const graphData = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const weeks = 18;
    const todayDayOfWeek = today.getDay(); // 0=Sunday, 6=Saturday

    // Calculate start date so that today lands at position (weeks-1, todayDayOfWeek)
    // Total days from start to today = (weeks-1)*7 + todayDayOfWeek
    const totalDaysFromStart = (weeks - 1) * 7 + todayDayOfWeek;
    const startDate = new Date(today);
    startDate.setDate(today.getDate() - totalDaysFromStart);

    const data = [];

    for (let w = 0; w < weeks; w++) {
      const weekData = [];
      for (let d = 0; d < 7; d++) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + (w * 7) + d);
        const dateString = date.toLocaleDateString('sv');

        const count = dateMap[dateString] || 0;
        weekData.push({
          date: dateString,
          count,
          level: count === 0 ? 0 : Math.min(Math.ceil(count / 10), 4)
        });
      }
      data.push(weekData);
    }

    return { weeks: data, monthLabels: generateMonthLabels };
  }, [dateMap, generateMonthLabels]);

  // The profile uses the same 18-week range as the detailed heatmap, but each
  // week collapses to one cell. This keeps the overview truthful while turning
  // a dashboard chart into the compact rhythm strip used by Me.
  const profileWeeks = useMemo(() => graphData.weeks.map((week) => {
    const count = week.reduce((total, day) => total + day.count, 0);
    return {
      count,
      date: `${week[0].date} – ${week[week.length - 1].date}`,
      level: Math.max(...week.map(day => day.level)),
    };
  }), [graphData.weeks]);

  useEffect(() => {
    if (!loading) onStatsChange?.(stats);
  }, [loading, onStatsChange, stats]);

  if (loading) {
    if (isProfile) {
      return (
        <div className="reading-heatmap variant-profile loading">
          <div className="profile-heatmap__header">
            <span className="profile-heatmap__label">{t('heatmap.streak')}</span>
            <span className="profile-heatmap__summary-skeleton" aria-hidden="true" />
          </div>
          <div className="profile-heatmap__graph profile-heatmap__graph--loading" aria-hidden="true">
            {Array.from({ length: 18 }).map((_, index) => (
              <span key={index} className="profile-heatmap__cell" />
            ))}
          </div>
        </div>
      );
    }

    return (
      <div className={`reading-heatmap loading ${variantClass}`}>
        <div className="heatmap-skeleton">
          {/* Stat labels with skeleton values - values shimmer, labels stable */}
          <StatLabelsSkeleton t={t} />
          {/* Skeleton graph: 18 weeks × 7 days */}
          <div className="skeleton-graph">
            {Array.from({ length: 18 }).map((_, w) => (
              <div key={w} className="skeleton-week">
                {Array.from({ length: 7 }).map((_, d) => (
                  <div key={d} className="skeleton-cell" />
                ))}
              </div>
            ))}
          </div>
          {/* Stable month labels */}
          <MonthLabels monthLabels={generateMonthLabels} variant={variant} />
        </div>
      </div>
    );
  }

  if (isProfile) {
    return (
      <div className="reading-heatmap variant-profile">
        <div className="profile-heatmap__header">
          <span className="profile-heatmap__label">{t('heatmap.streak')}</span>
          <span className="profile-heatmap__summary">
            <span><strong>{stats.totalBlocks}</strong> {t('heatmap.blocks')}</span>
            <span className="profile-heatmap__separator" aria-hidden="true">·</span>
            <span><strong>{marksCount}</strong> {t('heatmap.marks')}</span>
            <span className="profile-heatmap__separator" aria-hidden="true">·</span>
            <span><strong>{stats.dayCount}</strong> {t('heatmap.days')}</span>
          </span>
        </div>

        <button
          type="button"
          className="profile-heatmap__graph"
          onClick={() => setShowPrediction(true)}
          onMouseLeave={hideTooltip}
          aria-label={`${t('heatmap.streak')}: ${stats.totalBlocks} ${t('heatmap.blocks')}, ${marksCount} ${t('heatmap.marks')}, ${stats.dayCount} ${t('heatmap.days')}`}
        >
          {profileWeeks.map((week) => (
            <span
              key={week.date}
              className={`profile-heatmap__cell level-${week.level}`}
              onMouseEnter={(event) => showTooltip(week, event)}
            />
          ))}
        </button>

        {tooltip.visible && createPortal(
          <div
            className="heatmap-tooltip"
            style={{
              position: 'fixed',
              left: `${tooltip.x}px`,
              top: `${tooltip.y}px`,
              transform: 'translate(-50%, -100%)',
            }}
          >
            <div className="tooltip-date">{tooltip.date}</div>
            <div className="tooltip-count">{t('heatmap.blocksCount', { count: tooltip.count })}</div>
          </div>,
          document.body
        )}

        <PredictionModal
          isOpen={showPrediction}
          onClose={() => setShowPrediction(false)}
          listId={listId}
        />
      </div>
    );
  }

  if (isSidebar && !loading && isCollapsed) {
    return (
      <button
        type="button"
        className="reading-heatmap reading-heatmap--collapsed"
        onClick={toggleCollapse}
        aria-expanded="false"
        aria-label={t('showMore')}
      >
        <div className="heatmap-collapsed-bar">
          <span className="collapsed-stats">
            {stats.totalBlocks} {t('heatmap.blocks').toLowerCase()} · {marksCount} {t('heatmap.marks').toLowerCase()} · {stats.dayCount} {t('heatmap.days').toLowerCase()}
          </span>
          <ChevronDown size={ICON_SIZE.xxs} className="heatmap-collapse-icon heatmap-collapse-icon--up" />
        </div>
      </button>
    );
  }

  return (
    <div className={`reading-heatmap ${variantClass}`}>
      <div className="heatmap-header">
        <MetricValue value={stats.totalBlocks} label={t('heatmap.blocks')} />
        <MetricValue value={marksCount} label={t('heatmap.marks')} title={t('heatmap.marksTitle')} />
        <MetricValue value={stats.dayCount} label={t('heatmap.days')} />
        {isSidebar && (
          <button
            type="button"
            className="heatmap-collapse-btn"
            onClick={toggleCollapse}
            aria-expanded="true"
            aria-label={t('showLess')}
          >
            <ChevronDown size={ICON_SIZE.xxs} />
          </button>
        )}
      </div>

      <div className="heatmap-graph" onClick={() => setShowPrediction(true)} style={{ cursor: 'pointer' }}>
        <div className="graph-grid">
          {graphData.weeks.map((week, wIndex) => (
            <div key={wIndex} className="graph-week">
              {week.map((day) => (
                <div
                  key={day.date}
                  className={`graph-cell level-${day.level}`}
                  onMouseEnter={(e) => showTooltip(day, e)}
                  onMouseLeave={hideTooltip}
                  onClick={(e) => handleCellClick(day, e)}
                />
              ))}
            </div>
          ))}
        </div>
        <MonthLabels monthLabels={graphData.monthLabels} variant={variant} />
      </div>

      {/* Custom Tooltip - rendered via portal to avoid scroll container issues */}
      {tooltip.visible && createPortal(
        <div
          className="heatmap-tooltip"
          style={{
            position: 'fixed',
            left: `${tooltip.x}px`,
            top: `${tooltip.y}px`,
            transform: 'translate(-50%, -100%)',
          }}
        >
          <div className="tooltip-date">{tooltip.date}</div>
          <div className="tooltip-count">{t('heatmap.blocksCount', { count: tooltip.count })}</div>
        </div>,
        document.body
      )}

      {/* Prediction Modal */}
      <PredictionModal
        isOpen={showPrediction}
        onClose={() => setShowPrediction(false)}
        listId={listId}
      />
    </div>
  );
};

export default ReadingHeatmap;
